import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/auth.service';
import { LogedUser } from '@base/src/app/core/models/logged-user.model';
import { ChangeCurrency } from 'src/app/models/change-currency.model';
import { Constants } from 'src/app/models/constants';
import { IQrResponse } from 'src/app/models/IQrResponse.model';
import { QRButton } from 'src/app/models/qr-button.model';
import { QRToggle } from 'src/app/models/qr-toggle.model';
import { ChangeCurrencyService } from 'src/app/services/change-currency.service';
import { GetCurrencyService } from 'src/app/services/get-currency.service';
import { AUTH_SESSION } from 'src/app/core/auth-flow/tokens';
import { AuthSession } from 'src/app/core/auth-flow/auth-session';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { environment } from '@base/environments/environment';
import { QRIcon } from '../../models/qr-icon.model';

export interface WebModule {
  id: number;
  label: any;
  url: string;
  icon?: string;
  roles: string[];
  order: number;
  dropdown?: boolean;
}

@Component({
  selector: 'app-panel-layout',
  templateUrl: './panel-layout.component.html',
  styleUrls: ['./panel-layout.component.scss'],
})
export class PanelLayoutComponent implements OnInit {
  loggedUserSubscription: Subscription | undefined;
  userSelectedSubscription: Subscription | undefined;
  loggedUser: LogedUser | undefined;
  userDisplayName: string = '-';
  openToolsVar: boolean = false;

  URL_HOME: string = '/home';
  URL_CONFIGURATION: string = '/configuration';
  URL_SUPPORT: string = '/support';

  readonly ICON_ACCOUNT_CIRCLE = Constants.ICONS.ACCOUNT_CIRCLE;
  readonly ICON_DASHBOARD = Constants.ICONS.DASHBOARD;
  readonly ICON_TIMELINE = Constants.ICONS.TIMELINE;
  readonly ICON_TUNE = Constants.ICONS.TUNE;
  readonly ICON_PERSONS = Constants.ICONS.PERSONS;
  readonly ICON_SWAP_HORIZ = Constants.ICONS.SWAP_HORIZ;
  readonly ICON_BANK = Constants.ICONS.BANK;
  readonly ICON_ASSIGNMENT = Constants.ICONS.ASSIGNMENT;
  readonly ICON_ANALYTICS = Constants.ICONS.ANALYTICS;
  readonly ICON_DESCRIPTION = Constants.ICONS.DESCRIPTION;
  readonly ICON_SETTINGS = Constants.ICONS.SETTINGS;
  readonly ICON_PASSWORD = Constants.ICONS.PASSWORD;
  readonly ICON_SUPPORT = Constants.ICONS.SUPPORT;
  readonly ICON_LOGOUT = Constants.ICONS.LOGOUT;
  readonly ICON_PROPERTIES = Constants.ICONS.PROPERTIES;
  readonly ICON_TABLE_CHART = Constants.ICONS.TABLE_CHART;
  readonly ICON_COMISIONES_COBRADAS = Constants.ICONS.COMISIONES_COBRADAS;

  // Constants Colors
  readonly COLOR_GREY_400 = QRIcon.COLOR_GREY_400;

  accountButton: QRButton = {
    hierarchy: QRButton.HIERARCHY_4,
    size: QRButton.SIZE_MEDIUM,
    icon: Constants.ICONS.ACCOUNT_CIRCLE,
    iconOnly: true,
  };

  visibleMenuUser: boolean = false;

  toggleCurrency: QRToggle = {
    hierarchy: QRToggle.HIERARCHY_1,
    size: QRToggle.SIZE_SMALL,
    viewValues: [Constants.INTERNATIONAL_CURRENCY, Constants.LOCAL_CURRENCY],
    optionSelected: 0,
  };
  currencySelected: string | undefined;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly router: Router,
    @Inject(AUTH_SESSION) private readonly authSession: AuthSession,
    private readonly userSessionService: UserSessionService,
    private readonly changeCurrencyService: ChangeCurrencyService,
    private readonly getCurrencyService: GetCurrencyService,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit() {
    this.validateLoggedUser();
    this.getLoggedUser();
    this.getUserData();
  }

  getUserData() {
    this.loggedUserSubscription = this.userSessionService.loggedUser$.subscribe(
      (loggedUser) => {
        if (loggedUser) {
          if (loggedUser.role != 1) {
            this.router.navigate(['/']);
          } else {
            this.userDisplayName = loggedUser.name;

            this.getCurrencySelected();
            this.userSelectedSubscription =
              this.userSessionService.userSelected$.subscribe((userSelected) => {
                // Al cambiar de oficina/usuario (listUsers)
                if (userSelected != null) {
                  this.userDisplayName = userSelected.userFullName;
                }
              });
          }
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.loggedUserSubscription?.unsubscribe();
    this.userSelectedSubscription?.unsubscribe();
  }

  getLoggedUser(): void {
    const LOGGED_USER_JSON: string | null = localStorage.getItem(
      UserSessionService.LOGGED_USER
    );
    if (LOGGED_USER_JSON) {
      const loggedUser: LogedUser | undefined = JSON.parse(LOGGED_USER_JSON);
      this.loggedUser = loggedUser;
    }
  }

  validateLoggedUser(): void {
    this.authSession
      .restoreSession()
      .then((response: LogedUser) => {
        this.userSessionService.loggedUser = response;
      })
      .catch(() => {
        this.userSessionService.userSelected = null;
        this.router.navigate(['session-expired']);
      });
  }

  logout(): void {
    this.authService.logout();
  }

  isConfigVisible(): boolean {
    let isVisible: boolean = false;
    if (
      this.loggedUser?.security.agency_conf_notification ||
      this.loggedUser?.security.agency_conf_rest
    ) {
      isVisible = true;
    }
    return isVisible;
  }

  getCurrencySelected(): void {
    let currencySelected: string | null | undefined = localStorage.getItem(
      UserSessionService.SELECTED_CURRENCY
    );
    if (currencySelected == null) {
      if (this.loggedUser?.id) {
        let user: { user: string } = {
          user: this.loggedUser?.id,
        };
        this.getCurrencyService
          .postNoId(user)
          .then((response) => {
            let dataResponse: IQrResponse<ChangeCurrency> = response.body;
            this.currencySelected = dataResponse.data.currency;
            this.userSessionService.currencySelected = this.currencySelected; // Se guarda la moneda en almacenamiento de sesión
          })
          .finally(() => {
            this.setToggleCurrency();
          });
      }
    } else {
      // Ya fue guardado en el almacenamiento de sesión la moneda
      this.currencySelected = JSON.parse(currencySelected);
      this.setToggleCurrency();
    }
  }

  setToggleCurrency(): void {
    this.toggleCurrency = {
      ...this.toggleCurrency,
      optionSelected:
        this.currencySelected === Constants.CURRENCY_DOLLAR ? 0 : 1,
    };
  }

  toggleCurrencyChange(option: number): void {
    this.currencySelected = Constants.CURRENCY_DOLLAR;

    if (option != 0) {
      this.currencySelected = Constants.CURRENCY_PESOS_ARS;
    }
    if (this.loggedUser?.id) {
      let changeCurrecy: ChangeCurrency = {
        user: this.loggedUser?.id,
        currency: this.currencySelected,
      };
      this.changeCurrencyService
        .postNoId(changeCurrecy)
        .then(() => {})
        .finally(() => {
          this.userSessionService.currencySelected = this.currencySelected;

          this.snackbarService.openSnackBar(
            'Se ha cambiado la moneda a ' + this.currencySelected,
            ''
          );
        });
    }
  }
}
